import { useEffect, useState } from "react";
import api, { fileUrl } from "../../lib/api";
import { Plus, Trash2, Pencil, Save, X, Upload as UploadIcon, Copy } from "lucide-react";

const empty = { name: "", role: "", bio: "", image_url: "", order: 0 };

export default function LeadershipEditor() {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);
    const load = () => api.get("/leadership").then(({ data }) => setItems(data));
    useEffect(() => { load(); }, []);

    const save = async (l) => {
        const payload = { ...l, order: parseInt(l.order) || 0 };
        if (l.id) await api.put(`/leadership/${l.id}`, payload); else await api.post("/leadership", payload);
        setEditing(null); load();
    };
    const del = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/leadership/${id}`); load(); };
    const dup = async (id) => { await api.post(`/leadership/${id}/duplicate`); load(); };

    return (
        <div data-testid="leadership-editor">
            <div className="flex items-center justify-between mb-8">
                <div><h1 className="font-display text-4xl mb-2">Leadership</h1><p className="text-muted-foreground">Manage the team shown on the About page.</p></div>
                <button onClick={() => setEditing({ ...empty })} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-sm bg-primary text-primary-foreground text-sm font-medium" data-testid="add-leader"><Plus size={14} /> New Member</button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {items.map((l) => (
                    <div key={l.id} className="bg-card border border-border rounded-sm overflow-hidden">
                        {l.image_url && <img src={fileUrl(l.image_url)} alt={l.name} className="aspect-[4/3] w-full object-cover" />}
                        <div className="p-5">
                            <h3 className="font-display text-xl mb-1">{l.name}</h3>
                            <div className="text-xs uppercase tracking-[0.18em] text-primary mb-3">{l.role}</div>
                            <p className="text-sm text-muted-foreground mb-4 line-clamp-3">{l.bio}</p>
                            <div className="flex gap-2">
                                <button onClick={() => setEditing({ ...l })} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-sm border border-border text-xs"><Pencil size={12} /> Edit</button>
                                <button onClick={() => dup(l.id)} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-sm border border-border text-xs" data-testid={`dup-leader-${l.id}`}><Copy size={12} /> Duplicate</button>
                                <button onClick={() => del(l.id)} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-sm border border-destructive text-destructive text-xs"><Trash2 size={12} /> Delete</button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
            {editing && <Modal item={editing} onChange={setEditing} onSave={save} onClose={() => setEditing(null)} />}
        </div>
    );
}

const Modal = ({ item, onChange, onSave, onClose }) => {
    const set = (k, v) => onChange({ ...item, [k]: v });
    const upload = async (file) => {
        const fd = new FormData(); fd.append("file", file);
        const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
        set("image_url", data.url);
    };
    return (
        <div className="fixed inset-0 z-50 bg-foreground/50 flex items-start justify-center p-6 overflow-y-auto">
            <div className="bg-card border border-border rounded-sm w-full max-w-2xl my-10">
                <div className="flex items-center justify-between p-6 border-b border-border">
                    <h2 className="font-display text-2xl">{item.id ? "Edit Member" : "New Member"}</h2>
                    <button onClick={onClose} className="p-2 rounded-sm hover:bg-muted"><X size={18} /></button>
                </div>
                <div className="p-6 space-y-5">
                    <F label="Name" value={item.name} onChange={(v) => set("name", v)} testid="leader-name" />
                    <F label="Role" value={item.role} onChange={(v) => set("role", v)} testid="leader-role" />
                    <F label="Order" type="number" value={item.order} onChange={(v) => set("order", v)} testid="leader-order" />
                    <T label="Bio" value={item.bio} onChange={(v) => set("bio", v)} testid="leader-bio" />
                    <div>
                        <span className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-2 block">Photo</span>
                        <div className="flex items-center gap-3">
                            <input value={item.image_url || ""} onChange={(e) => set("image_url", e.target.value)} className="flex-1 px-3 py-2.5 rounded-sm border border-border bg-background text-sm" />
                            <label className="inline-flex items-center gap-2 px-4 py-2.5 rounded-sm border border-border cursor-pointer text-sm"><UploadIcon size={14} /> Upload
                                <input type="file" className="hidden" accept="image/*" onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} />
                            </label>
                        </div>
                        {item.image_url && <img src={fileUrl(item.image_url)} alt="" className="mt-3 h-32 rounded-sm border border-border object-cover" />}
                    </div>
                </div>
                <div className="p-6 border-t border-border flex justify-end gap-3">
                    <button onClick={onClose} className="px-5 py-2.5 rounded-sm border border-border text-sm">Cancel</button>
                    <button onClick={() => onSave(item)} className="inline-flex items-center gap-2 px-6 py-2.5 rounded-sm bg-primary text-primary-foreground text-sm font-medium"><Save size={14} /> Save</button>
                </div>
            </div>
        </div>
    );
};

const F = ({ label, value, onChange, testid, type = "text" }) => (
    <label className="block"><span className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-2 block">{label}</span>
        <input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="w-full px-3 py-2.5 rounded-sm border border-border bg-background text-sm" /></label>
);
const T = ({ label, value, onChange, testid }) => (
    <label className="block"><span className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-2 block">{label}</span>
        <textarea rows={4} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="w-full px-3 py-2.5 rounded-sm border border-border bg-background text-sm resize-y" /></label>
);
